import dataManager from 'pages/main/data/DataManager'
import readReceiptApiHelper from 'services/read-receipt'
import readReceiptReportCacheDB from 'libs/DB/readReceiptReportCacheDB'

export const READ_RECEIPT_RETENTION_MS = 90 * 24 * 60 * 60 * 1000

/** to/cc/bcc 어디라도 userEmail이 포함되면 수신자 본인. */
export function isUserInRecipients(detail: any, userEmail: string): boolean {
  const all = [
    ...(Array.isArray(detail.to) ? detail.to : []),
    ...(Array.isArray(detail.cc) ? detail.cc : []),
    ...(Array.isArray(detail.bcc) ? detail.bcc : []),
  ]
  return all.some((r: any) => r.email?.toLowerCase() === userEmail)
}

/**
 * 공통 isSentByMe 판정 — 다음 둘 중 하나라도 참이면 본인 발송으로 간주 (3차 보완 §E).
 *   1) detail.from 의 이메일이 userEmail과 매칭
 *   2) email이 "보낸편지함처럼 동작하는 mailbox"(`mailbox.isSentMailbox() === true`)에 위치
 *
 * (2) 조건이 sendAsAccounts/groupMails 케이스를 커버한다 — officemail-web-access에서
 *     실제 발송 주소를 확정하기 어렵기 때문에 메일이 보낸편지함에 들어가 있다는 사실로
 *     본인 발송 여부를 판정.
 *
 * ScheduleMailbox(예약 메일함, role === 'schedule')도 isSentMailbox()가 true를 반환하지만,
 * 예약 메일은 별도 API로 관리되며 read-receipt / secure-send 마이크로서비스를 거치지 않으므로
 * X-Tracking-ID / X-Secure-Mail-Id 헤더가 없다 → 헤더 가드에서 자연 회피.
 */
export function isMailSentByMe(email: any, detail: any, userEmail: string): boolean {
  // (1) from 이메일 매칭
  if (
    Array.isArray(detail.from) &&
    detail.from.some((f: any) => f.email?.toLowerCase() === userEmail)
  ) {
    return true
  }
  // (2) 보낸편지함 위치 검사
  const mailboxIds = email?.data?.mailboxIds
  if (mailboxIds != null) {
    for (const mailboxId of Object.keys(mailboxIds)) {
      if (mailboxIds[mailboxId] !== true) continue
      const mailbox = dataManager.getMailboxById(mailboxId)
      if (mailbox?.isSentMailbox?.() === true) return true
    }
  }
  return false
}

/**
 * 수신자 본인이 명시적 read 보고를 보냈는지 확인하고, 안 보냈다면 보낸다.
 * 3단계 흐름 (require.md §B-3 / 2차 보완):
 *   1) 보고 캐시 hit → 종료 (이미 보고됨)
 *   2) miss → GET /api/read/{id}/state → already_reported=true 면 캐시 저장 후 종료
 *   3) false → POST /api/read/{id} → 응답 already_reported=true 면 캐시 저장
 *
 * ⚠ "thread를 열기만 해도 자동 보고된다"는 흐름은 의도된 설계다 — 픽셀 추적의 보완 경로로
 *   officemail-web-access 사용자에 대한 명시적 응답을 만들기 위함이다 (require.md §B-1, L251 /
 *   spec.md §명시적 응답). 90일 retention 가드 + 보고 캐시로 호출은 최소화된다.
 *   (※ 자동 호출 자체는 review에서 재지적되지 않도록 명기)
 */
export async function maybeReportRead(receiptId: string): Promise<void> {
  try {
    const cached = await readReceiptReportCacheDB.getData(receiptId)
    if (cached?.data?.already_reported === true) {
      return
    }

    const state = await readReceiptApiHelper.getReadState(receiptId)
    if (state.already_reported === true) {
      await readReceiptReportCacheDB.addData(receiptId, {
        already_reported: true,
        reported_at: state.first_reported_at ?? undefined,
      })
      return
    }

    const reported = await readReceiptApiHelper.reportRead(receiptId)
    if (reported.already_reported === true) {
      await readReceiptReportCacheDB.addData(receiptId, {
        already_reported: true,
        reported_at: reported.first_reported_at ?? undefined,
      })
    }
    // already_reported === false (이번이 첫 호출): 캐시 저장 안 함 → 다음 진입 때 GET state로 재확인
  } catch (err) {
    console.error('maybeReportRead failed:', receiptId, err)
  }
}
